/**
 * DesignerShellPage — Phase 13 P4 cutover wrapper.
 *
 * The canonical 10-line page body that legacy designer pages
 * branch into when `isShellV2On()` returns true. This component:
 *
 *   1. Mounts a `DesignerProvider` over the existing zustand
 *      sceneStore so every registered tool gets the live scene.
 *   2. Renders `DesignerShell` with the page-supplied `canvasSlot`
 *      (Konva for football, video player for video scenes, etc.).
 *   3. Loads the scene from the Scenario OS API when `sceneId` is
 *      provided and populates the sceneStore.
 */
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DesignerProvider } from './DesignerContext';
import { DesignerShell } from './DesignerShell';
import { bootstrapDesignerToolbox } from './bootstrap';
import { useSceneStore } from '../store/sceneStore';
import { sceneAPI } from '../services/api';

export function DesignerShellPage({
  canvasSlot,
  sceneId = null,
  userRoles = [],
  domainPack = null,
  appSurface = 'coach',
}) {
  const [searchParams] = useSearchParams();
  const loadScene = useSceneStore((s) => s.loadScene);

  useEffect(() => { bootstrapDesignerToolbox(); }, []);

  useEffect(() => {
    if (!sceneId) return;
    let cancelled = false;

    const fetchScene = async () => {
      try {
        const res = await sceneAPI.get(sceneId);
        if (!cancelled) {
          loadScene(res.data);
        }
      } catch (err) {
        console.warn('[DesignerShellPage] scene fetch error:', err);
      }
    };

    fetchScene();
    return () => { cancelled = true; };
  }, [sceneId, loadScene]);

  // Wire save persistence: listen for designer.save_request on the
  // global bus and PATCH the scene via the API.
  useEffect(() => {
    if (!sceneId) return;
    const bus = window.__flowgenix_designer_bus__;
    if (!bus) return;

    const handleSave = async ({ scene }) => {
      if (!scene) return;
      try {
        await sceneAPI.update(sceneId, scene);
        useSceneStore.getState().markClean();
      } catch (err) {
        console.error('[DesignerShellPage] save failed:', err);
      }
    };

    const unsub = bus.on('designer.save_request', handleSave);
    return () => { if (typeof unsub === 'function') unsub(); else bus.off('designer.save_request', handleSave); };
  }, [sceneId]);

  // Wire preview: open the learner session in a new tab when Preview is clicked.
  // Derives the learn-app origin from the current coach-app origin:
  //   coach.X.Y.com → learn.X.Y.com
  //   any other origin → same origin (works for local / localhost dev)
  useEffect(() => {
    const bus = window.__flowgenix_designer_bus__;
    if (!bus) return;

    const handlePreview = ({ scene: s }) => {
      const targetScene = s || useSceneStore.getState().scene;
      if (!targetScene) return;
      const sid = targetScene.scene_id || sceneId;
      const did = targetScene.domain_id || '';
      const origin = window.location.origin;
      // coach.* → learn.* ; everything else keeps the same origin
      const learnOrigin = origin.replace(/^(https?:\/\/)coach\./, '$1learn.');
      // The learner-app is always mounted at /training (PUBLIC_URL from its build).
      // The coach-app has its own origin and doesn't set REACT_APP_BASE_PATH,
      // so we hard-code the learner base path here.
      const learnerBase = '/training';
      const url = `${learnOrigin}${learnerBase}/session/${did}/${sid}?mode=simulation&from=coach_preview`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const unsub = bus.on('designer.preview_request', handlePreview);
    return () => { if (typeof unsub === 'function') unsub(); else bus.off('designer.preview_request', handlePreview); };
  }, [sceneId]);

  const domainFromUrl = searchParams.get('domain');
  const resolvedDomainPack = domainPack || (domainFromUrl ? { id: domainFromUrl } : null);

  return (
    <DesignerProvider
      appSurface={appSurface}
      userRoles={userRoles}
      domainPack={resolvedDomainPack}
    >
      <DesignerShell canvasSlot={canvasSlot} />
    </DesignerProvider>
  );
}

export default DesignerShellPage;
